import React, { useEffect, useState } from 'react';

import { Controller, useForm } from 'react-hook-form';

import FormHeader from 'prequalificationV2/components/FormHeader';
import {
  LoadingType,
  PreQualificationStep,
  setLoadingState,
  setPreQualificationStep,
} from 'prequalificationV2/actions';
import { setCoBorrowerChangedInfoAfterProvePreFill } from 'prequalificationV2/actions/application';
import { setCoBorrower } from 'prequalificationV2/actions/coBorrower';
import { Button } from 'components/Buttons/Button';
import FormBody from 'components/FormBody';
import Input from 'components/Inputs/Input';
import NumberInput from 'components/Inputs/NumberInput';
import PhoneNumberInput from 'components/Inputs/PhoneNumberInput';
import Loader from 'components/Loader';
import { ButtonType } from 'enums/ButtonType';
import { LoanInformationVariables } from 'prequalificationV2/enums/LoanInformationVariables';
import { useAppDispatch } from 'prequalificationV2/hooks/useAppDispatch';
import { useAppSelector } from 'prequalificationV2/hooks/useAppSelector';
import {
  MASKED_SSN_LENGTH,
  BORROWER_FIRST_LAST_NAME_MAX_LENGTH,
  SSN_MAX_LENGTH,
} from 'prequalificationV2/utils/constants';
import {
  getErrorMessageForRequiredFields,
  maskSSN,
  MONETARY_PREFIX,
  removeNonDigits,
  removeSpaces,
} from 'prequalificationV2/utils/stringUtils';
import {
  getMessageForInvalidFields,
  isValidEmail,
  validatePhoneNumber,
} from 'prequalificationV2/utils/validationUtils';
import { checkBirthDate } from 'prequalificationV2/utils/dateUtils';
import {
  validateFirstName,
  validateLastName,
} from 'prequalificationV2/pages/PreQualification/steps/BorrowerInformation/borrowerInformation.utils';
import { EyeIcon } from 'components/Icons';
import { Checkbox, CheckboxType } from 'components/Checkbox';
import * as notificationManager from 'notifications/index';
import { FailureNotificationType } from 'notifications/index';
import { BorrowerDto } from 'prequalificationV2/api/BorrowersApi';
import { ErrorDto } from 'errors/ErrorDto';
import { ErrorCodes } from 'enums/ErrorCodes';
import { preQualificationApi } from 'prequalificationV2/api/PreQualification/PreQualificationApi';
import { mapCoBorrowerToUnderwritingCoBorrowerDto } from 'prequalificationV2/api/PreQualification/preQualificationApi.utils';
import { marketingApi } from 'api/MarketingApi';
import { PrequalificationVersion } from 'enums/ApplicationsVariables';
import { ConceptualHqEvent } from 'enums/ConceptualHqEvent';
import { getConceptualDeviceId } from 'prequalificationV2/routes/routes.utils';
import { useNavigateAfterUnderwriting } from 'prequalificationV2/hooks/useNavigateAfterUnderwriting';

import styles from './CoBorrowerInformation.module.scss';

interface CoBorrowerInformationProps {
  isAdditionalCoBorrower?: boolean;
}

const CoBorrowerInformation = ({ isAdditionalCoBorrower = false }: CoBorrowerInformationProps) => {
  const dispatch = useAppDispatch();
  const { handleUnderwritingResult } = useNavigateAfterUnderwriting();

  const loading = useAppSelector((state) => state.loading[LoadingType.GetData]);

  const {
    application,
    borrower: {
      borrowerId,
      streetName: borrowerStreetName,
      streetNumber: borrowerStreetNumber,
      city: borrowerCity,
      zip: borrowerZip,
      state: borrowState,
      personalIdNumber: borrowerSSN,
      county: borrowerCounty,
      email: borrowerEmail,
      phone: borrowerPhone,
    },
    coBorrower,
    coBorrower: { firstName, lastName, personalIdNumber, phone, dateOfBirth, email },
  } = useAppSelector((store) => store);

  const { register, setValue, trigger, watch, control, formState, clearErrors } = useForm({
    defaultValues: {
      [LoanInformationVariables.CoBorrowerFirstName]: firstName,
      [LoanInformationVariables.CoBorrowerLastName]: lastName,
      [LoanInformationVariables.CoBorrowerSSN]: personalIdNumber,
      [LoanInformationVariables.CoBorrowerCellPhone]: phone,
      [LoanInformationVariables.CoBorrowerDateOfBirth]: dateOfBirth,
      [LoanInformationVariables.CoBorrowerEmail]: email,
      [LoanInformationVariables.FullConsentCheckbox]: false,
      [LoanInformationVariables.CoBorrowerIncome]: '',
    },
  });
  const watcher = watch();
  const isSsnManualEntry = isAdditionalCoBorrower || !!application![LoanInformationVariables.IsCoBorrowerManualFlow];
  const [isSsnVisible, setSsnVisible] = useState(!isSsnManualEntry);

  const handleInputChange = () => {
    dispatch(setCoBorrowerChangedInfoAfterProvePreFill(true));
  };

  const checkMatchWithBorrowerSSN = (value: string) => {
    return value !== borrowerSSN || 'Must be different from Borrower SSN';
  };

  const checkMatchWithBorrowerEmail = (value: string) => {
    return value?.toLowerCase() !== borrowerEmail || 'Must be different from Borrower Email';
  };

  useEffect(() => {
    register(LoanInformationVariables.CoBorrowerFirstName, {
      required: getErrorMessageForRequiredFields(LoanInformationVariables.CoBorrowerFirstName),
      validate: (value) => {
        return validateFirstName(value, LoanInformationVariables.CoBorrowerFirstName);
      },
    });
    register(LoanInformationVariables.CoBorrowerLastName, {
      required: getErrorMessageForRequiredFields(LoanInformationVariables.CoBorrowerLastName),
      validate: (value) => {
        return validateLastName(value, LoanInformationVariables.CoBorrowerLastName);
      },
    });
    register(LoanInformationVariables.CoBorrowerSSN, {
      required: getErrorMessageForRequiredFields(LoanInformationVariables.CoBorrowerSSN),
      validate: (value) =>
        value.length === SSN_MAX_LENGTH
          ? checkMatchWithBorrowerSSN(value)
          : getMessageForInvalidFields(LoanInformationVariables.SSN),
    });
    register(LoanInformationVariables.CoBorrowerCellPhone, {
      required: getErrorMessageForRequiredFields(LoanInformationVariables.CoBorrowerCellPhone),
      validate: (value) => {
        if (borrowerPhone === value) {
          return 'Must be different from Borrower Cell Phone';
        }

        return validatePhoneNumber(value);
      },
    });
    register(LoanInformationVariables.CoBorrowerDateOfBirth, {
      required: getErrorMessageForRequiredFields(LoanInformationVariables.CoBorrowerDateOfBirth),
      validate: (value) => checkBirthDate(value) || 'Please enter a valid date',
    });
    register(LoanInformationVariables.CoBorrowerEmail, {
      required: getErrorMessageForRequiredFields(LoanInformationVariables.CoBorrowerEmail),
      validate: (value) =>
        isValidEmail(value) ? checkMatchWithBorrowerEmail(value) : 'Please enter a valid email address',
    });
    register(LoanInformationVariables.FullConsentCheckbox, {
      required: false,
    });

    if (isAdditionalCoBorrower) {
      register(LoanInformationVariables.CoBorrowerIncome, {
        required: 'Income is required',
      });
    }
  }, [register, watcher]);

  useEffect(() => {
    if (isSsnManualEntry) {
      dispatch(
        setCoBorrower({
          eligible: false,
        }),
      );
    }

    const track = async () => {
      const deviceId = await getConceptualDeviceId();

      if (!deviceId) {
        return;
      }

      marketingApi
        .trackConceptualHqEvent({
          event: ConceptualHqEvent.Coborrower,
          prequalificationVersion: PrequalificationVersion.Second,
          borrowerId: borrowerId,
          deviceId: deviceId,
        })
        .catch(() => {});
    };

    track();
  }, []);

  const goToNextStep = () => {
    dispatch(setPreQualificationStep(PreQualificationStep.LoanInformation));
  };

  const handleSubmit = async () => {
    const result = await trigger();

    if (result) {
      const coborrowerPayload = {
        firstName: watcher[LoanInformationVariables.CoBorrowerFirstName].trim(),
        lastName: watcher[LoanInformationVariables.CoBorrowerLastName].trim(),
        personalIdNumber: watcher[LoanInformationVariables.CoBorrowerSSN],
        dateOfBirth: watcher[LoanInformationVariables.CoBorrowerDateOfBirth],
        email: watcher[LoanInformationVariables.CoBorrowerEmail],
        phone: watcher[LoanInformationVariables.CoBorrowerCellPhone],
        ...(isAdditionalCoBorrower && {
          annualIncome: +removeNonDigits(watcher[LoanInformationVariables.CoBorrowerIncome]),
        }),
      };

      dispatch(
        setCoBorrower({
          ...coborrowerPayload,
          fullConsentAgreed: !!watcher[LoanInformationVariables.FullConsentCheckbox],
          consentAgreed: true,
        }),
      );

      dispatch(setLoadingState({ type: LoadingType.GetData, loading: true }));

      try {
        if (isAdditionalCoBorrower) {
          const response = await preQualificationApi.addCoborrower(
            application.applicationId as string,
            mapCoBorrowerToUnderwritingCoBorrowerDto({
              ...coBorrower,
              ...coborrowerPayload,
            }),
          );

          await handleUnderwritingResult(response);

          await preQualificationApi.updateBorrowerMarketingConsent({
            email: watcher[LoanInformationVariables.CoBorrowerEmail],
            fullConsentAgreed: !!watcher[LoanInformationVariables.FullConsentCheckbox],
            consentAgreed: true,
          });

          return;
        }

        const coborrowerShortInfo = await preQualificationApi.getCoborrowerShortInfoBySsn({
          ssn: watcher[LoanInformationVariables.CoBorrowerSSN],
        });

        if (coborrowerShortInfo.id) {
          const { hasBorrowerApplications, hasCoborrowerApplications } = coborrowerShortInfo;

          if (hasBorrowerApplications || hasCoborrowerApplications || coborrowerShortInfo.locked) {
            dispatch(setPreQualificationStep(PreQualificationStep.CoBorrowerAlreadyHaveActiveApplication));

            return;
          } else {
            dispatch(setCoBorrower({ borrowerId: coborrowerShortInfo.id }));
            goToNextStep();
          }
        } else {
          const newCoBorrower = await preQualificationApi.createBorrower({
            firstName: watcher[LoanInformationVariables.CoBorrowerFirstName],
            lastName: watcher[LoanInformationVariables.CoBorrowerLastName],
            personalIdNumber: watcher[LoanInformationVariables.CoBorrowerSSN],
            phone: watcher[LoanInformationVariables.CoBorrowerCellPhone],
            email: watcher[LoanInformationVariables.CoBorrowerEmail],
            address: {
              streetName: borrowerStreetName,
              streetNumber: borrowerStreetNumber!,
              city: borrowerCity,
              stateOrProvince: borrowState,
              zipOrPostalCode: borrowerZip,
              county: borrowerCounty,
            },
            dateOfBirth: watcher[LoanInformationVariables.CoBorrowerDateOfBirth],
            fullConsentAgreed: !!watcher[LoanInformationVariables.FullConsentCheckbox],
          });

          if ((newCoBorrower as ErrorDto)?.error?.errorCode === ErrorCodes.EMAIL_IS_ALREADY_USED) {
            notificationManager.notifyFailure(FailureNotificationType.CoBorrowerEmailAlreadyExists);

            return;
          }

          const borrower = newCoBorrower as BorrowerDto;

          dispatch(setCoBorrower({ borrowerId: borrower.id as string }));
          goToNextStep();
        }

        await preQualificationApi.updateBorrowerMarketingConsent({
          email: watcher[LoanInformationVariables.CoBorrowerEmail],
          fullConsentAgreed: !!watcher[LoanInformationVariables.FullConsentCheckbox],
          consentAgreed: true,
        });
      } catch (error: any) {
        notificationManager.notifyFailure(FailureNotificationType.UnknownError);
      } finally {
        dispatch(setLoadingState({ type: LoadingType.GetData, loading: false }));
      }
    }
  };

  const handleBack = () => {
    dispatch(
      setPreQualificationStep(
        isAdditionalCoBorrower ? PreQualificationStep.PromptToAddCoBorrower : PreQualificationStep.BorrowerInfo,
      ),
    );
  };
  const handleCopyPaste = (event: React.ClipboardEvent) => event.preventDefault();

  return (
    <>
      {loading ? (
        <Loader />
      ) : (
        <>
          <div className={styles.infoContainer}>
            <div className={styles.infoContent}>THIS INQUIRY DOES NOT AFFECT YOUR CREDIT SCORE</div>
          </div>
          <FormHeader title="Coborrower" percent={37} step={2} totalSteps={4} />
          <FormBody>
            <div className={styles.rowContainer}>
              <Input
                label={LoanInformationVariables.CoBorrowerFirstName}
                errorMessage={formState.errors[LoanInformationVariables.CoBorrowerFirstName]?.message}
                className={styles.formInput}
                name={LoanInformationVariables.CoBorrowerFirstName}
                onChange={(event) => {
                  handleInputChange();
                  setValue(LoanInformationVariables.CoBorrowerFirstName, event.target.value);
                  trigger(LoanInformationVariables.CoBorrowerFirstName);
                }}
                value={watcher[LoanInformationVariables.CoBorrowerFirstName]}
                maxLength={BORROWER_FIRST_LAST_NAME_MAX_LENGTH}
              />
              <Input
                label={LoanInformationVariables.CoBorrowerLastName}
                errorMessage={formState.errors[LoanInformationVariables.CoBorrowerLastName]?.message}
                className={styles.formInput}
                name={LoanInformationVariables.CoBorrowerLastName}
                onChange={(event) => {
                  handleInputChange();
                  setValue(LoanInformationVariables.CoBorrowerLastName, event.target.value);
                  trigger(LoanInformationVariables.CoBorrowerLastName);
                }}
                value={watcher[LoanInformationVariables.CoBorrowerLastName]}
                maxLength={BORROWER_FIRST_LAST_NAME_MAX_LENGTH}
              />
            </div>
            <div className={styles.rowContainer}>
              <Input
                label={LoanInformationVariables.CoBorrowerSSN}
                className={styles.formInput}
                name={LoanInformationVariables.CoBorrowerSSN}
                type={isSsnVisible ? 'text' : 'password'}
                onChange={(event) => {
                  handleInputChange();

                  const value = removeNonDigits(event.target.value.trim());

                  setValue(LoanInformationVariables.CoBorrowerSSN, value);

                  clearErrors(LoanInformationVariables.CoBorrowerSSN);

                  if (formState.isSubmitted) {
                    trigger(LoanInformationVariables.CoBorrowerSSN);
                  }
                }}
                errorMessage={formState.errors[LoanInformationVariables.CoBorrowerSSN]?.message}
                value={
                  isSsnManualEntry
                    ? maskSSN(watcher[LoanInformationVariables.CoBorrowerSSN], !isSsnVisible)
                    : `***-**-${watcher[LoanInformationVariables.CoBorrowerSSN].substr(5)}`
                }
                disabled={!isSsnManualEntry}
                maxLength={isSsnVisible ? MASKED_SSN_LENGTH : SSN_MAX_LENGTH}
                inputMode="numeric"
                endAdornment={() =>
                  isSsnManualEntry ? (
                    <EyeIcon
                      open={isSsnVisible}
                      className={styles.eyeIcon}
                      onClick={() => {
                        setSsnVisible(!isSsnVisible);
                      }}
                    />
                  ) : (
                    <></>
                  )
                }
              />
              <NumberInput
                label={LoanInformationVariables.CoBorrowerDateOfBirth}
                placeholder="MM/DD/YYYY"
                errorMessage={formState.errors[LoanInformationVariables.CoBorrowerDateOfBirth]?.message}
                className={styles.formInput}
                name={LoanInformationVariables.CoBorrowerDateOfBirth}
                onChange={(event) => {
                  clearErrors(LoanInformationVariables.CoBorrowerDateOfBirth);
                  setValue(LoanInformationVariables.CoBorrowerDateOfBirth, event.target.value);
                }}
                onBlur={() => {
                  if (watcher[LoanInformationVariables.CoBorrowerDateOfBirth]) {
                    trigger(LoanInformationVariables.CoBorrowerDateOfBirth);
                  }
                }}
                format="##/##/####"
                value={watcher[LoanInformationVariables.CoBorrowerDateOfBirth]}
                mask="_"
                allowEmptyFormatting
              />
            </div>
            <div className={styles.rowContainer}>
              <PhoneNumberInput
                label={LoanInformationVariables.CoBorrowerCellPhone}
                errorMessage={formState.errors[LoanInformationVariables.CoBorrowerCellPhone]?.message}
                className={styles.formInput}
                name={LoanInformationVariables.CoBorrowerCellPhone}
                onChange={(value: string) => {
                  clearErrors([LoanInformationVariables.CoBorrowerCellPhone]);
                  setValue(LoanInformationVariables.CoBorrowerCellPhone, value);

                  if (formState.isSubmitted) {
                    trigger(LoanInformationVariables.CoBorrowerCellPhone);
                  }
                }}
                value={watcher[LoanInformationVariables.CoBorrowerCellPhone]}
                onBlur={() => {
                  if (watcher[LoanInformationVariables.CoBorrowerCellPhone]) {
                    trigger(LoanInformationVariables.CoBorrowerCellPhone);
                  }
                }}
              />
              <Input
                label={LoanInformationVariables.CoBorrowerEmail}
                errorMessage={formState.errors[LoanInformationVariables.CoBorrowerEmail]?.message}
                className={styles.formInput}
                name={LoanInformationVariables.CoBorrowerEmail}
                onChange={(event: any) => {
                  clearErrors([LoanInformationVariables.CoBorrowerEmail]);
                  setValue(LoanInformationVariables.CoBorrowerEmail, removeSpaces(event.target.value?.toLowerCase()));

                  if (formState.isSubmitted) {
                    trigger(LoanInformationVariables.CoBorrowerEmail);
                  }
                }}
                value={watcher[LoanInformationVariables.CoBorrowerEmail]}
                onCopy={handleCopyPaste}
              />
            </div>
            {isAdditionalCoBorrower && (
              <div className={styles.incomeContainer}>
                <div className={styles.incomeHeaderContainer}>Income</div>
                <NumberInput
                  label={`Coborrower's Annual Income`}
                  prefix={MONETARY_PREFIX}
                  placeholder={MONETARY_PREFIX}
                  thousandSeparator
                  className={styles.incomeInput}
                  name={LoanInformationVariables.CoBorrowerIncome}
                  onChange={(event: any) => {
                    setValue(LoanInformationVariables.CoBorrowerIncome, event.target.value);
                    trigger(LoanInformationVariables.CoBorrowerIncome);
                  }}
                  errorMessage={formState.errors[LoanInformationVariables.CoBorrowerIncome]?.message}
                  value={watcher[LoanInformationVariables.CoBorrowerIncome]}
                  allowNegative={false}
                  onKeyDown={(event: React.KeyboardEvent<HTMLInputElement>) => {
                    if (event.key === 'Enter') {
                      event.preventDefault();
                    }
                  }}
                />
              </div>
            )}
            <div className={styles.consentText}>
              I agree to receive text messages from HFS Financial, LLC at the phone number provided. These messages may
              include transactional communications, status updates, and appointment reminders. Message frequency may
              vary. Standard message and data rates may apply. Reply STOP at any time to unsubscribe. For help, reply
              HELP or contact us at 800-254-9560. Please review our{' '}
              <a href="https://www.hfsfinancial.net/privacy-policy/" target="_blank" rel="noopener noreferrer">
                Privacy Policy
              </a>{' '}
              to learn how your data is used.
            </div>
            <Controller
              name={LoanInformationVariables.FullConsentCheckbox}
              control={control}
              rules={{
                required: false,
              }}
              render={() => (
                <div className={styles.checkboxContainer}>
                  <Checkbox
                    className={styles.checkboxText}
                    type={CheckboxType.Alternative}
                    name="agreementCheckbox"
                    isChecked={!!watcher[LoanInformationVariables.FullConsentCheckbox]}
                    onChange={() => {
                      setValue(
                        LoanInformationVariables.FullConsentCheckbox,
                        !watcher[LoanInformationVariables.FullConsentCheckbox],
                      );
                    }}
                    text={`Co-Borrower expressly consents to receiving calls and messages, including auto-dialed and pre-recorded
                     message calls, and SMS messages (including text messages) from HFS Financial at any telephone numbers
                      that you have provided or may provide in the future (including any cellular telephone numbers).  
                      HFS Financial may send text messages in various formats and with various contents, including 
                      but not limited to, text messages about products, services, and transactions you have requested/initiated; 
                      and/or marketing the products and services offered by or through us. You are the owner or authorized
                       user of the mobile phone number you have provided and you will notify us immediately 
                       if you are no longer the owner or authorized user of the mobile phone number you have provided.
                        Your cellular or mobile telephone provider will charge you according to the type of plan you carry.`}
                    labelClassName={styles.checkboxTextContainer}
                  />
                </div>
              )}
            />
          </FormBody>
          <div className={styles.navigationButtons}>
            <Button className={styles.backButton} buttonType={ButtonType.Secondary} withArrow onClick={handleBack}>
              Back
            </Button>
            <Button onClick={handleSubmit} withArrow>
              Continue
            </Button>
          </div>
        </>
      )}
    </>
  );
};

export default CoBorrowerInformation;
